import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp';
import { motion } from 'framer-motion';
import { Clock, CheckCircle, AlertCircle, Home, RefreshCw } from 'lucide-react';
import { lawyerService } from '../services/firebaseService';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import toast from 'react-hot-toast';

const PendingApprovalScreen = () => {
  const navigate = useNavigate();
  const { language } = useApp();
  const { user: currentUser } = useFirebaseAuth();
  const [lawyerProfile, setLawyerProfile] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const translations = {
    english: {
      title: 'Profile Pending Approval',
      subtitle: 'Your lawyer profile is under review',
      description: 'Our admin team is reviewing your profile and documents. This usually takes 24-48 hours. You\'ll be notified once your profile is approved.',
      status: 'Status: Pending Review',
      checkStatus: 'Check Status',
      goHome: 'Go Home',
      contactSupport: 'Contact Support',
      estimatedTime: 'Estimated Review Time: 24-48 hours',
      whatHappensNext: 'What happens next?',
      nextSteps: [
        'Admin reviews your profile and documents',
        'Verification of bar council license',
        'Background check completion',
        'Profile approval and activation'
      ]
    },
    hindi: {
      title: 'प्रोफाइल मंजूरी लंबित',
      subtitle: 'आपका वकील प्रोफाइल समीक्षा के अधीन है',
      description: 'हमारी एडमिन टीम आपके प्रोफाइल और दस्तावेजों की समीक्षा कर रही है। इसमें आमतौर पर 24-48 घंटे लगते हैं। आपको सूचित किया जाएगा जब आपका प्रोफाइल मंजूर हो जाए।',
      status: 'स्थिति: समीक्षा लंबित',
      checkStatus: 'स्थिति जांचें',
      goHome: 'होम पर जाएं',
      contactSupport: 'सहायता से संपर्क करें',
      estimatedTime: 'अनुमानित समीक्षा समय: 24-48 घंटे',
      whatHappensNext: 'आगे क्या होता है?',
      nextSteps: [
        'एडमिन आपके प्रोफाइल और दस्तावेजों की समीक्षा करता है',
        'बार काउंसिल लाइसेंस का सत्यापन',
        'पृष्ठभूमि जांच पूर्णता',
        'प्रोफाइल मंजूरी और सक्रियण'
      ]
    }
  };

  const t = translations[language] || translations.english;

  useEffect(() => {
    loadLawyerProfile();
  }, [currentUser]);

  // Auto-check for approval status every 30 seconds
  useEffect(() => {
    if (!currentUser?.uid) return;

    const checkApprovalStatus = async () => {
      try {
        console.log('🔄 Auto-checking approval status for UID:', currentUser.uid);
        const result = await lawyerService.getLawyerByUserId(currentUser.uid);
        console.log('📊 Auto-check result:', result);
        
        if (result.success && result.data) {
          setLawyerProfile(result.data);
          console.log('📋 Current lawyer profile data:', result.data);
          console.log('🔍 isVerified status:', result.data.isVerified);
          console.log('🔍 verificationStatus:', result.data.verificationStatus);
          
          // If profile is approved, redirect immediately
          if (result.data.isVerified || result.data.verificationStatus === 'approved') {
            console.log('✅ Lawyer profile approved! Auto-redirecting to dashboard...');
            toast.success('Profile approved! Redirecting to dashboard...');
            navigate('/lawyer-dashboard');
          } else {
            console.log('⏳ Profile still pending approval...');
          }
        } else {
          console.log('❌ No lawyer profile found in auto-check');
        }
      } catch (error) {
        console.error('❌ Error auto-checking approval status:', error);
      }
    };

    // Check immediately
    checkApprovalStatus();

    // Set up interval to check every 10 seconds (faster for testing)
    const interval = setInterval(checkApprovalStatus, 10000);

    return () => clearInterval(interval);
  }, [currentUser?.uid, navigate]);

  const loadLawyerProfile = async () => {
    if (!currentUser?.uid) return;
    
    setIsLoading(true);
    setError(null);

    try {
      const result = await lawyerService.getLawyerByUserId(currentUser.uid);
      if (result.success && result.data) {
        setLawyerProfile(result.data);
      } else {
        setError('Failed to load lawyer profile. Please try again.');
      }
    } catch (error) {
      setError('Error loading lawyer profile: ' + error.message);
      console.error('Error loading lawyer profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckStatus = async () => {
    setIsChecking(true);
    try {
      console.log('🔄 Manual check status for UID:', currentUser?.uid);
      await loadLawyerProfile();
      
      // Check if profile is now approved
      if (lawyerProfile?.isVerified || lawyerProfile?.verificationStatus === 'approved') {
        console.log('✅ Lawyer profile approved! Redirecting to dashboard...');
        toast.success('Profile approved! Redirecting to dashboard...');
        navigate('/lawyer-dashboard');
        return;
      }
      
      // Show updated status
      console.log('⏳ Profile still pending approval. Current status:', lawyerProfile);
      toast.success('Status checked. Still pending approval.');
    } catch (error) {
      console.error('❌ Error checking status:', error);
      toast.error('Failed to check status. Please try again.');
    } finally {
      setIsChecking(false);
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleContactSupport = () => {
    // You can implement contact support logic here
    window.open('mailto:support@nyayshayak.com?subject=Lawyer Profile Approval Query', '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex flex-col justify-center items-center px-6 py-8 relative overflow-hidden">
      {/* Background blur effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-100/20 via-transparent to-indigo-100/20 dark:from-purple-900/10 dark:via-transparent dark:to-indigo-900/10 backdrop-blur-sm"></div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 text-center w-full max-w-md mx-auto"
      >
        {/* Loading State */}
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading profile data...</p>
          </motion.div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6"
          >
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">{error}</span>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={loadLawyerProfile}
                className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Retry
              </button>
              <button
                onClick={() => navigate('/lawyer-registration')}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Complete Registration
              </button>
            </div>
          </motion.div>
        )}

        {/* Main Content - Only show when not loading and no errors */}
        {!isLoading && !error && (
          <>
            {/* Status Icon */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center mb-6"
            >
              <div className="flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg">
                <Clock className="w-10 h-10 text-white" />
              </div>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-3xl font-bold text-gray-900 dark:text-white mb-4"
            >
              {t.title}
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-4"
            >
              {t.subtitle}
            </motion.p>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-sm text-gray-600 dark:text-gray-400 mb-6 leading-relaxed"
            >
              {t.description}
            </motion.p>

            {/* Status Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mb-6"
            >
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 border border-purple-200 dark:border-purple-700">
                <div className="w-2 h-2 bg-purple-500 rounded-full mr-2 animate-pulse"></div>
                <span className="text-sm font-medium">{t.status}</span>
              </div>
            </motion.div>

            {/* Estimated Time */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="mb-8"
            >
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t.estimatedTime}
              </p>
            </motion.div>

            {/* What happens next */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="mb-8 text-left"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                {t.whatHappensNext}
              </h3>
              <ul className="space-y-2">
                {t.nextSteps.map((step, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="space-y-4"
            >
                       <button
               onClick={handleCheckStatus}
               disabled={isChecking}
               className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
             >
               {isChecking ? (
                 <RefreshCw className="w-5 h-5 animate-spin" />
               ) : (
                 <CheckCircle className="w-5 h-5" />
               )}
               {isChecking ? 'Checking...' : t.checkStatus}
             </button>

             

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleGoHome}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-200"
              >
                <Home className="w-4 h-4" />
                {t.goHome}
              </button>

              <button
                onClick={handleContactSupport}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-200"
              >
                <AlertCircle className="w-4 h-4" />
                {t.contactSupport}
              </button>
            </div>
          </motion.div>
        </>
        )}

        {/* Decorative Elements */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="absolute top-10 left-10 w-20 h-20 bg-purple-200 rounded-full opacity-20 blur-xl"
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="absolute bottom-10 right-10 w-16 h-16 bg-indigo-200 rounded-full opacity-20 blur-xl"
        />
      </motion.div>
    </div>
  );
};

export default PendingApprovalScreen;
